// ============================================================================
// BOQ — Library archives (boq.library_versions + its four child tables)
// ============================================================================
// An archive is a full frozen copy of the rate library: items, item_versions
// (with long specs), classifications and rates. It holds NO foreign key back
// to the live rows, so editing the library never reaches an archive and
// viewing an archive never touches the library.
//
// Snapshot and restore both live in the database
// (boq.snapshot_library_version / boq.restore_library_version) because the
// restore has to reason about FK references — including bill_version_lines,
// which points at item_versions with no constraint at all. See
// supabase/migrations/20260910000001_boq_library_versioning.sql.
//
// RLS: any signed-in user reads, admin/supervisor writes.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { formatSupabaseError } from '../../hooks/useSupabase';
import { buildClassificationMaps, fetchAllRows } from '../utils/libraryClassification';

const VERSION_COLUMNS = 'id, seq, code, label, is_auto, snapshot_at, created_by, item_count, version_count, rate_count';

// Archived item_versions carry their long specs, so an archive view needs no
// lazy per-trade detail fetch the way the live library does.
const SNAP_VERSION_COLUMNS =
  'src_version_id, item_code, version_letter, full_code, short_description, ' +
  'long_spec_general, long_spec_products, long_spec_execution, unit, measurement_ref, ' +
  'status, change_reason, source_document_id';

const mapArchive = (r) => ({
  id: r.id,
  seq: r.seq,
  code: r.code,
  label: r.label,
  isAuto: r.is_auto,
  snapshotAt: r.snapshot_at,
  createdBy: r.created_by,
  itemCount: r.item_count,
  versionCount: r.version_count,
  rateCount: r.rate_count,
});

// Shaped to match useBoqLibrary's mapItem / mapVersion so LibraryPage can read
// an archive and the live library through the same props.
const mapSnapItem = (i) => ({
  itemCode: i.item_code,
  trade: i.trade,
  subtrade: i.subtrade,
  sequence: i.sequence,
  status: i.status,
  createdAt: null,
  updatedAt: null,
});

const mapSnapVersion = (v) => ({
  id: v.src_version_id,
  itemCode: v.item_code,
  versionLetter: v.version_letter,
  fullCode: v.full_code,
  shortDescription: v.short_description,
  longSpecGeneral: v.long_spec_general,
  longSpecProducts: v.long_spec_products,
  longSpecExecution: v.long_spec_execution,
  unit: v.unit,
  measurementRef: v.measurement_ref,
  status: v.status,
  changeReason: v.change_reason,
  sourceDocumentId: v.source_document_id,
  createdAt: null,
  updatedAt: null,
});

const requireSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data?.session) return 'Please sign in to manage library archives.';
  return null;
};

export const useBoqLibraryVersions = ({ enabled = true } = {}) => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);          // a snapshot / restore is running
  // Whether this user may delete an archive. Answered by the same DB function
  // the RLS delete policies use, so the button and the database can't disagree;
  // defaults to false so the control is hidden until the grant is confirmed.
  const [canDelete, setCanDelete] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data, error: err }, grant] = await Promise.all([
        supabase.schema('boq').from('library_versions').select(VERSION_COLUMNS).order('seq', { ascending: false }),
        supabase.schema('boq').rpc('can_delete_library_version'),
      ]);
      if (err) throw err;
      setArchives((data || []).map(mapArchive));
      // A failed grant check is treated as "no" rather than thrown: it must not
      // stop the archive list rendering, and hiding the button is the safe miss.
      setCanDelete(grant.error ? false : grant.data === true);
    } catch (err) {
      console.error('[useBoqLibraryVersions] fetch failed', err);
      setError(formatSupabaseError(err, { action: 'fetch', entity: 'library archives' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) fetchAll(); }, [enabled, fetchAll]);

  // Freeze the current library into a new archive.
  const createArchive = useCallback(async (label) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    setBusy(true);
    try {
      const { data, error: err } = await supabase
        .schema('boq').rpc('snapshot_library_version', { p_label: label || null, p_is_auto: false });
      if (err) throw err;
      await fetchAll();
      return { success: true, id: data };
    } catch (err) {
      console.error('[useBoqLibraryVersions] createArchive failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'create', entity: 'library archive' }) };
    } finally {
      setBusy(false);
    }
  }, [fetchAll]);

  // Deleting is limited to admins plus a named allow-list — see
  // boq.can_delete_library_version(). RLS is what actually enforces it; this
  // check only turns a silent no-op (RLS makes the delete match zero rows)
  // into a message the user can read.
  const deleteArchive = useCallback(async (id) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    if (!canDelete) return { success: false, error: 'You do not have permission to delete library versions.' };
    try {
      const { error: err } = await supabase.schema('boq').from('library_versions').delete().eq('id', id);
      if (err) throw err;
      setArchives((prev) => prev.filter((a) => a.id !== id));
      return { success: true };
    } catch (err) {
      console.error('[useBoqLibraryVersions] deleteArchive failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'delete', entity: 'library archive' }) };
    }
  }, [canDelete]);

  // Overwrite the live library with an archive. The RPC auto-archives the
  // current state first and returns a summary of what it changed.
  const restoreArchive = useCallback(async (id) => {
    const denied = await requireSession();
    if (denied) return { success: false, error: denied };
    setBusy(true);
    try {
      const { data, error: err } = await supabase
        .schema('boq').rpc('restore_library_version', { p_version_id: id });
      if (err) throw err;
      await fetchAll();
      return { success: true, summary: data };
    } catch (err) {
      console.error('[useBoqLibraryVersions] restoreArchive failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'restore', entity: 'library archive' }) };
    } finally {
      setBusy(false);
    }
  }, [fetchAll]);

  // Pull one archive in full, in the shape LibraryPage consumes. The
  // classification taxonomy is NOT archived — only the tags — so the archive
  // resolves against today's nodes, same builder as the live library.
  const fetchSnapshot = useCallback(async (id) => {
    try {
      const [itemRows, versionRows, classRows, rateRows, taxRes, nodesRes] = await Promise.all([
        fetchAllRows(() => supabase.schema('boq').from('library_version_items')
          .select('item_code, trade, subtrade, sequence, status').eq('version_id', id).order('item_code')),
        fetchAllRows(() => supabase.schema('boq').from('library_version_item_versions')
          .select(SNAP_VERSION_COLUMNS).eq('version_id', id).order('full_code')),
        // Ordered because fetchAllRows pages with .range() — an unordered page
        // boundary can drop or duplicate rows.
        fetchAllRows(() => supabase.schema('boq').from('library_version_classifications')
          .select('src_version_id, classification_node_id').eq('version_id', id)
          .order('src_version_id').order('classification_node_id')),
        fetchAllRows(() => supabase.schema('boq').from('library_version_rates')
          .select('item_version_id, rate_quoted, rate_billed, rate_date').eq('version_id', id)
          .order('src_rate_id')),
        supabase.schema('boq').from('classification_taxonomies').select('id, code'),
        supabase.schema('boq').from('classification_nodes')
          .select('id, taxonomy_id, code, label, sort_order, parent_id, maps_to_node_id, short_code, series_start, series_end').order('sort_order'),
      ]);
      if (taxRes.error) throw taxRes.error;
      if (nodesRes.error) throw nodesRes.error;

      const maps = buildClassificationMaps({
        // The builder keys tags by item_version id; archived rows carry theirs
        // as src_version_id, and the tag rows use the same column name.
        versionRows: versionRows.map((v) => ({ id: v.src_version_id, item_code: v.item_code })),
        classificationRows: classRows.map((c) => ({
          item_version_id: c.src_version_id,
          classification_node_id: c.classification_node_id,
        })),
        taxonomyRows: taxRes.data,
        nodeRows: nodesRes.data,
      });

      // Rate roll-up per item: count + most recent value, matching the live
      // library's aggregate so the Rate column reads identically.
      const codeByVersionId = new Map(versionRows.map((v) => [v.src_version_id, v.item_code]));
      const rateAggByItem = new Map();
      itemRows.forEach((i) => rateAggByItem.set(i.item_code, { count: 0, latestDate: '', latestVal: null }));
      rateRows.forEach((r) => {
        const code = codeByVersionId.get(r.item_version_id);
        if (!code) return;
        const val = r.rate_billed ?? r.rate_quoted;
        const cur = rateAggByItem.get(code) || { count: 0, latestDate: '', latestVal: null };
        cur.count += 1;
        if (val != null && (r.rate_date || '') >= cur.latestDate) {
          cur.latestDate = r.rate_date || '';
          cur.latestVal = Number(val);
          // The row reads its wording and unit from this version, so the
          // figure and the description shown beside it describe the same thing.
          cur.latestVersionId = r.item_version_id;
        }
        rateAggByItem.set(code, cur);
      });

      return {
        success: true,
        snapshot: {
          // Deprecated items are hidden in the live library, so hide them here
          // too — an archive should read the way that library read on the day.
          items: itemRows.filter((i) => i.status !== 'deprecated').map(mapSnapItem),
          versions: versionRows.map(mapSnapVersion),
          rateAggByItem,
          ...maps,
        },
      };
    } catch (err) {
      console.error('[useBoqLibraryVersions] fetchSnapshot failed', err);
      return { success: false, error: formatSupabaseError(err, { action: 'load', entity: 'library archive' }) };
    }
  }, []);

  return { archives, loading, error, busy, canDelete, refresh: fetchAll, createArchive, deleteArchive, restoreArchive, fetchSnapshot };
};
